import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, can, formatApiErrorDetail, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import DocViewer from "@/components/DocViewer";
import { Award, Eye, Download } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" });

export function SalarioCheck({ k, value, onChange, testid }) {
  return (
    <label className={`flex items-center gap-2 text-xs ${k.tiene_salario ? "text-slate-600" : "text-slate-300"}`} title={k.tiene_salario ? "" : "El contrato no tiene salario registrado"}>
      <input type="checkbox" disabled={!k.tiene_salario} checked={value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-vino-700" data-testid={testid} /> Incluir salario
    </label>
  );
}

export function CertificadosEmpleado({ vid }) {
  const { user } = useAuth();
  const puede = can(user, "certificados");
  const [data, setData] = useState(null);
  const [sal, setSal] = useState({});
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get(`/vinculaciones/${vid}/certificados`).then((r) => setData(r.data)).catch(() => setData({ contratos: [], certificados: [] }));
  useEffect(() => { load(); }, [vid]); // eslint-disable-line react-hooks/exhaustive-deps

  const emitir = async (k) => {
    setBusy(k.contratacion_id);
    try {
      const { data: rec } = await api.post("/certificados", { contratacion_id: k.contratacion_id, con_salario: !!sal[k.contratacion_id] });
      toast.success(`Certificado ${rec.consecutivo} generado`);
      await downloadFile(`/certificados/${rec.id}/download`, `certificado_${rec.consecutivo}.pdf`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!data) return <Spinner className="mx-auto my-8 h-5 w-5 text-vino-700" />;
  return (
    <div className="space-y-5" data-testid="empleado-certificados">
      {puede && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-slate-800">Generar certificado laboral</p>
          {!data.firma_disponible && <p className="text-xs text-amber-700" data-testid="emp-cert-sin-firma">Falta cargar y autorizar la firma institucional en Certificaciones.</p>}
          {data.contratos.length === 0 ? <p className="text-sm text-slate-400" data-testid="emp-cert-sin-contratos">Este empleado no tiene información laboral para certificar.</p> : data.contratos.map((k) => (
            <div key={k.contratacion_id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 p-3" data-testid={`emp-cert-contrato-${k.contratacion_id}`}>
              <div className="text-sm"><p className="font-semibold text-slate-800">{k.cargo || "—"} · {k.empresa_usuaria || "—"}</p><p className="text-xs text-slate-500">{k.fuente === "vinculacion" ? "Datos de Vinculación (sin contrato en Roge)" : k.numero_contrato} · Ingreso {k.fecha_ingreso || "—"} · {k.estado_laboral === "retirado" ? "Retirado" : "Activo"}</p>{!k.completo && <p className="text-xs text-amber-700">Falta: {k.faltantes.join(", ")}</p>}</div>
              <div className="flex flex-wrap items-center gap-2">
                <SalarioCheck k={k} value={!!sal[k.contratacion_id]} onChange={(v) => setSal((p) => ({ ...p, [k.contratacion_id]: v }))} testid={`emp-cert-salario-${k.contratacion_id}`} />
                <button disabled={!k.completo} onClick={() => setView(k)} className="rounded-lg border border-slate-200 p-2 text-slate-600 disabled:opacity-40" data-testid={`emp-cert-preview-${k.contratacion_id}`}><Eye className="h-4 w-4" /></button>
                <button disabled={!k.completo || !data.firma_disponible || !!busy} onClick={() => emitir(k)} className="flex items-center gap-1.5 rounded-lg bg-vino-700 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40" data-testid={`emp-cert-generar-${k.contratacion_id}`}><Award className="h-4 w-4" /> {busy === k.contratacion_id ? "Generando…" : "Generar certificado laboral"}</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-2">
        <p className="text-sm font-semibold text-slate-800">Certificados expedidos</p>
        {data.certificados.length === 0 ? <p className="text-sm text-slate-400">Sin certificados expedidos.</p> : data.certificados.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 py-2 last:border-0" data-testid={`emp-cert-emitido-${c.id}`}>
            <p className="text-sm"><span className="font-semibold text-slate-800">{c.consecutivo}</span> <span className="text-slate-500">· {c.tipo} · {fmt(c.fecha)} · {c.solicitado_por}</span>{c.estado === "anulado" && <span className="ml-2 rounded bg-red-50 px-1.5 text-[11px] font-semibold text-red-700">Anulado</span>}</p>
            <button onClick={() => downloadFile(`/certificados/${c.id}/download`, `certificado_${c.consecutivo}.pdf`)} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs text-slate-700" data-testid={`emp-cert-descargar-${c.id}`}><Download className="h-3.5 w-3.5" /> Descargar</button>
          </div>
        ))}
      </div>
      {view && <DocViewer url={`/certificados/preview/${view.contratacion_id}?con_salario=${!!sal[view.contratacion_id]}`} titulo="Vista previa del certificado" subtitulo={view.nombre} nombreArchivo="vista_previa_certificado.pdf" onClose={() => setView(null)} />}
    </div>
  );
}
